import React, {useEffect, useState} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import RCInput from '../../../utils/RCInput'
import RCSelect from '../../../utils/RCSelect'
import RCMultiSelect from '../../../utils/RCMultiSelect'
import RCDatePickerInput from '../../../utils/RCDatePickerInput'
import RCSwitch from '../../../utils/RCSwitch'
import PhotoUploadField from './shared/PhotoUploadField'
import {RACARequest} from '../../../core/api/Request'
import talentFormSignUpRequest from '../../../core/api/requests/public-signup/talentFormSignUpRequest'
import uploadSignUpFileRequest from '../../../core/api/requests/public-signup/uploadSignUpFileRequest'
import {useDispatch} from 'react-redux'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es obligatorio'),
  lastName: Yup.string().required('Los apellidos son obligatorios'),
  email: Yup.string().email('Email no válido').required('El email es obligatorio'),
  phone: Yup.string().required('El teléfono es obligatorio'),
  instagram: Yup.string(),
  city: Yup.string().required('La ciudad es obligatoria'),
  province: Yup.string().required('La provincia es obligatoria'),
  origin: Yup.array().min(1, 'El origen es obligatorio').required('El origen es obligatorio'),
  ethnicity: Yup.array().min(1, 'La etnia es obligatoria').required('La etnia es obligatoria'),
  birthDate: Yup.string().required('La fecha de nacimiento es obligatoria'),
  gender: Yup.string().required('El género es obligatorio'),
  hairColor: Yup.string().required('El color de pelo es obligatorio'),
  eyesColor: Yup.string().required('El color de ojos es obligatorio'),
  height: Yup.number().required('La altura es obligatoria').positive('Debe ser un número positivo'),
  bust: Yup.number().required('El pecho es obligatorio').positive('Debe ser un número positivo'),
  waist: Yup.number().required('La cintura es obligatoria').positive('Debe ser un número positivo'),
  hip: Yup.number().required('La cadera es obligatoria').positive('Debe ser un número positivo'),
  dressSize: Yup.string().required('La talla de camisa es obligatoria'),
  pantSize: Yup.string().required('La talla de pantalón es obligatoria'),
  shoesSize: Yup.string().required('La talla de zapatos es obligatoria'),
  driverLicence: Yup.array().min(1, 'El carné de conducir es obligatorio').required('El carné de conducir es obligatorio'),
  ownCar: Yup.boolean(),
})

const AlumnosSignupForm = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const {agencyId} = useParams<{agencyId: string}>()
  const {executeRecaptcha} = useGoogleReCaptcha()

  const getValuesAsArray = (min: number, max: number): number[] => {
    const values: number[] = []
    for (let i = min; i <= max; i++) {
      values.push(i)
    }
    return values
  }

  const clothingSizeOptions = [
    ...[3, 6, 9, 12, 18, 24].map((m) => ({ key: `${m} meses`, value: `${m} meses` })),
    ...getValuesAsArray(3, 16).map((y) => ({ key: `${y} años`, value: `${y} años` })),
    ...getValuesAsArray(32, 60).map((s) => ({ key: s.toString(), value: s.toString() })),
  ]

  const [futils, setFutils] = useState<any>(null)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [photoRetrato, setPhotoRetrato] = useState<File | null>(null)
  const [photoVertical1, setPhotoVertical1] = useState<File | null>(null)
  const [photoVertical2, setPhotoVertical2] = useState<File | null>(null)
  const [photoVertical3, setPhotoVertical3] = useState<File | null>(null)
  const [photoVertical4, setPhotoVertical4] = useState<File | null>(null)

  useEffect(() => {
    const req = new RACARequest('utils', 'futilsPublic')
    req.makeRequest().then((res: any) => {
      if (res.data) setFutils(res.data)
    })
  }, [])

  const formik = useFormik({
    initialValues: {
      name: '',
      lastName: '',
      email: '',
      phone: '',
      instagram: '',
      city: '',
      province: '',
      origin: [] as string[],
      ethnicity: [] as string[],
      birthDate: '',
      gender: '',
      hairColor: '',
      eyesColor: '',
      height: '' as any,
      bust: '' as any,
      waist: '' as any,
      hip: '' as any,
      dressSize: '',
      pantSize: '',
      shoesSize: '',
      driverLicence: [] as string[],
      ownCar: false,
      languages: [] as string[],
    },
    validationSchema,
    onSubmit: async (values) => {
      setError(null)

      if (!photoRetrato || !photoVertical1 || !photoVertical2 || !photoVertical3 || !photoVertical4) {
        setError('Todas las fotos son obligatorias')
        return
      }

      try {
        let recaptchaToken = ''
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('talent_form_signup')
        }

        const payload = {
          formType: 'fashion',
          recaptchaToken,
          name: values.name,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          instagram: values.instagram,
          city: values.city,
          province: values.province,
          origin: values.origin,
          ethnicity: values.ethnicity,
          birthDate: values.birthDate ? new Date(values.birthDate).toISOString() : '',
          gender: values.gender,
          hairColor: values.hairColor,
          eyesColor: values.eyesColor,
          height: values.height,
          bust: values.bust,
          waist: values.waist,
          hip: values.hip,
          dressSize: values.dressSize,
          pantSize: values.pantSize,
          shoesSize: values.shoesSize,
          driverLicence: values.driverLicence,
          ownCar: values.ownCar,
          languages: values.languages,
          modelTypes: ['Fashion'],
        }

        const response = await talentFormSignUpRequest(dispatch, payload, agencyId)

        if (!response.data || !response.data.id) {
          setError('Error al enviar el formulario. Inténtalo de nuevo.')
          return
        }

        const userId = response.data.id

        const photos = [photoRetrato, photoVertical1, photoVertical2, photoVertical3, photoVertical4]
        for (const photo of photos) {
          await uploadSignUpFileRequest(dispatch, userId, photo, 'photo', 'photo')
        }

        setSubmitted(true)
      } catch (e) {
        setError('Error al enviar el formulario. Inténtalo de nuevo.')
      }
    },
  })

  if (submitted) {
    return (
      <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
        <div className='card mb-5 mb-xl-10'>
          <div className='card-body pt-9 pb-0'>
            <div className='d-flex flex-column align-items-center py-15'>
              <h2 className='fw-bolder text-dark mb-5'>Formulario enviado correctamente</h2>
              <p className='text-muted fs-5'>Gracias por registrarte. Nos pondremos en contacto contigo pronto.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='container-fluid px-4 px-md-10 py-10' style={{maxWidth: '1400px', margin: '0 auto'}}>
      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Header */}
        <div className='card mb-5'>
          <div className='card-header border-0'>
            <div className='card-title m-0'>
              <h3 className='fw-bolder m-0'>Formulario de Inscripción - Alumnos</h3>
            </div>
          </div>
        </div>

        {/* SECTION: Información Personal */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Información Personal</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Nombre */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Nombre <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('name')}
                  i18n={'PLACEHOLDERS.NAME'}
                  value={formik.values.name || ''}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className='text-danger mt-2'>{formik.errors.name}</div>
                )}
              </div>

              {/* Apellidos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Apellidos <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('lastName')}
                  i18n={'PLACEHOLDERS.LAST_NAME'}
                  value={formik.values.lastName || ''}
                />
                {formik.touched.lastName && formik.errors.lastName && (
                  <div className='text-danger mt-2'>{formik.errors.lastName}</div>
                )}
              </div>

              {/* Email */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Email <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('email')}
                  i18n={'PLACEHOLDERS.EMAIL'}
                  value={formik.values.email || ''}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='text-danger mt-2'>{formik.errors.email}</div>
                )}
              </div>

              {/* Teléfono */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Teléfono <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('phone')}
                  i18n={'PLACEHOLDERS.PHONE'}
                  value={formik.values.phone || ''}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <div className='text-danger mt-2'>{formik.errors.phone}</div>
                )}
              </div>

              {/* Instagram */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Instagram</label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('instagram')}
                  i18n={'PLACEHOLDERS.INSTAGRAM'}
                  value={formik.values.instagram || ''}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Datos Personales */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Datos Personales</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Ciudad */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Ciudad <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={
                    futils?.talents?.contact?.residenceCity?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('city')}
                  i18n={'PLACEHOLDERS.CITY'}
                  value={formik.values.city}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className='text-danger mt-2'>{formik.errors.city}</div>
                )}
              </div>

              {/* Provincia */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Provincia <span className='text-danger'>*</span>
                </label>
                <RCInput
                  hideLabel
                  fieldsProps={formik.getFieldProps('province')}
                  i18n={'PLACEHOLDERS.PROVINCE'}
                  value={formik.values.province || ''}
                />
                {formik.touched.province && formik.errors.province && (
                  <div className='text-danger mt-2'>{formik.errors.province}</div>
                )}
              </div>

              {/* Origen */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Origen <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={
                    futils?.talents?.personalInformation?.nationalities?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('origin')}
                  i18n={'PLACEHOLDERS.ORIGIN'}
                  value={formik.values.origin}
                  onChange={(v: string[]) => formik.setFieldValue('origin', v)}
                />
                {formik.touched.origin && formik.errors.origin && (
                  <div className='text-danger mt-2'>{String(formik.errors.origin)}</div>
                )}
              </div>

              {/* Etnia */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Etnia <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={
                    futils?.talents?.personalInformation?.ethnicity?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('ethnicity')}
                  i18n={'PLACEHOLDERS.ETHNICITIES'}
                  value={formik.values.ethnicity}
                  onChange={(v: string[]) => formik.setFieldValue('ethnicity', v)}
                />
                {formik.touched.ethnicity && formik.errors.ethnicity && (
                  <div className='text-danger mt-2'>{String(formik.errors.ethnicity)}</div>
                )}
              </div>

              {/* Fecha de nacimiento */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Fecha de nacimiento <span className='text-danger'>*</span>
                </label>
                <RCDatePickerInput
                  allDay
                  hideLabel
                  fieldsProps={formik.getFieldProps('birthDate')}
                  i18n={'PLACEHOLDERS.BIRTH_DATE'}
                  value={formik.values.birthDate}
                />
                {formik.touched.birthDate && formik.errors.birthDate && (
                  <div className='text-danger mt-2'>{formik.errors.birthDate}</div>
                )}
              </div>

              {/* Género */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Género <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={
                    futils?.talents?.personalInformation?.gender?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('gender')}
                  i18n={'PLACEHOLDERS.GENDER'}
                  value={formik.values.gender}
                />
                {formik.touched.gender && formik.errors.gender && (
                  <div className='text-danger mt-2'>{formik.errors.gender}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Características Físicas */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Características Físicas</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Pelo */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Pelo <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={
                    futils?.talents?.physicalCharacteristics?.hairColor?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('hairColor')}
                  i18n={'PLACEHOLDERS.HAIR_COLOR'}
                  value={formik.values.hairColor}
                />
                {formik.touched.hairColor && formik.errors.hairColor && (
                  <div className='text-danger mt-2'>{formik.errors.hairColor}</div>
                )}
              </div>

              {/* Ojos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Ojos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={
                    futils?.talents?.physicalCharacteristics?.eyesColor?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('eyesColor')}
                  i18n={'PLACEHOLDERS.EYES_COLOR'}
                  value={formik.values.eyesColor}
                />
                {formik.touched.eyesColor && formik.errors.eyesColor && (
                  <div className='text-danger mt-2'>{formik.errors.eyesColor}</div>
                )}
              </div>

              {/* Altura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Altura (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(50, 220).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('height')}
                  i18n={'PLACEHOLDERS.HEIGHT'}
                  value={formik.values.height}
                />
                {formik.touched.height && formik.errors.height && (
                  <div className='text-danger mt-2'>{String(formik.errors.height)}</div>
                )}
              </div>

              {/* Pecho */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Pecho (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('bust')}
                  i18n={'PLACEHOLDERS.BUST'}
                  value={formik.values.bust}
                />
                {formik.touched.bust && formik.errors.bust && (
                  <div className='text-danger mt-2'>{String(formik.errors.bust)}</div>
                )}
              </div>

              {/* Cintura */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Cintura (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('waist')}
                  i18n={'PLACEHOLDERS.WAIST'}
                  value={formik.values.waist}
                />
                {formik.touched.waist && formik.errors.waist && (
                  <div className='text-danger mt-2'>{String(formik.errors.waist)}</div>
                )}
              </div>

              {/* Cadera */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Cadera (cm) <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(20, 200).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('hip')}
                  i18n={'PLACEHOLDERS.HIP'}
                  value={formik.values.hip}
                />
                {formik.touched.hip && formik.errors.hip && (
                  <div className='text-danger mt-2'>{String(formik.errors.hip)}</div>
                )}
              </div>

              {/* Talla de camisa */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de camisa <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('dressSize')}
                  i18n={'PLACEHOLDERS.DRESS_SIZE'}
                  value={formik.values.dressSize}
                />
                {formik.touched.dressSize && formik.errors.dressSize && (
                  <div className='text-danger mt-2'>{formik.errors.dressSize}</div>
                )}
              </div>

              {/* Talla de pantalón */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de pantalón <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={clothingSizeOptions}
                  hideLabel
                  fieldsProps={formik.getFieldProps('pantSize')}
                  i18n={'PLACEHOLDERS.PANT_SIZE'}
                  value={formik.values.pantSize}
                />
                {formik.touched.pantSize && formik.errors.pantSize && (
                  <div className='text-danger mt-2'>{formik.errors.pantSize}</div>
                )}
              </div>

              {/* Talla de zapatos */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Talla de zapatos <span className='text-danger'>*</span>
                </label>
                <RCSelect
                  options={getValuesAsArray(10, 50).map((v) => ({key: v.toString(), value: v.toString()}))}
                  hideLabel
                  fieldsProps={formik.getFieldProps('shoesSize')}
                  i18n={'PLACEHOLDERS.SHOES_SIZE'}
                  value={formik.values.shoesSize}
                />
                {formik.touched.shoesSize && formik.errors.shoesSize && (
                  <div className='text-danger mt-2'>{formik.errors.shoesSize}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Otros */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Otros</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              {/* Carné de conducir */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>
                  Carné de conducir <span className='text-danger'>*</span>
                </label>
                <RCMultiSelect
                  options={
                    futils?.talents?.skills?.driverLicence?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('driverLicence')}
                  i18n={'PLACEHOLDERS.DRIVER_LICENSE'}
                  value={formik.values.driverLicence}
                  onChange={(v: string[]) => formik.setFieldValue('driverLicence', v)}
                />
                {formik.touched.driverLicence && formik.errors.driverLicence && (
                  <div className='text-danger mt-2'>{String(formik.errors.driverLicence)}</div>
                )}
              </div>

              {/* Coche propio */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <RCSwitch
                  label='Coche propio'
                  fieldsProps={formik.getFieldProps('ownCar')}
                  i18n={'PLACEHOLDERS.OWN_CAR'}
                  value={formik.values.ownCar}
                />
              </div>

              {/* Idiomas */}
              <div className='col-lg-4 col-md-6 mb-6'>
                <label className='form-label fw-bold fs-6'>Idiomas</label>
                <RCMultiSelect
                  options={
                    futils?.talents?.language?.languages?.map((o: string) => ({key: o, value: intl.formatMessage({id: o})})) || []
                  }
                  hideLabel
                  fieldsProps={formik.getFieldProps('languages')}
                  i18n={'PLACEHOLDERS.LANGUAGES'}
                  value={formik.values.languages}
                  onChange={(v: string[]) => formik.setFieldValue('languages', v)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION: Archivos */}
        <div className='card mb-5'>
          <div className='card-header'>
            <h3 className='card-title'>Archivos</h3>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto RETRATO en blanco y negro'
                  file={photoRetrato}
                  onFileSelect={setPhotoRetrato}
                  required
                />
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto vertical 1'
                  file={photoVertical1}
                  onFileSelect={setPhotoVertical1}
                  required
                />
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto vertical 2'
                  file={photoVertical2}
                  onFileSelect={setPhotoVertical2}
                  required
                />
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto vertical 3'
                  file={photoVertical3}
                  onFileSelect={setPhotoVertical3}
                  required
                />
              </div>

              <div className='col-lg-4 col-md-6 mb-6'>
                <PhotoUploadField
                  label='Foto vertical 4'
                  file={photoVertical4}
                  onFileSelect={setPhotoVertical4}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className='mb-6'>
            <div className='text-danger fw-bold'>{error}</div>
          </div>
        )}

        {/* Submit */}
        <div className='d-flex justify-content-end py-6'>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  )
}

export {AlumnosSignupForm}
